import { useEffect, useState } from 'react';
import { type HighlightedCitation } from '@frontend/api-call-intel';
import type { ConversationSection } from '../types';

type UseConversationNavigationParams = {
  initialCitation?: HighlightedCitation;
  showTranscriptOnly: boolean;
};

const hasHighlightedCitation = (highlightedCitation?: HighlightedCitation): boolean =>
  !!highlightedCitation?.text && !!highlightedCitation?.time;

const getDefaultSection = ({
  highlightedCitation,
  showTranscriptOnly,
}: {
  highlightedCitation?: HighlightedCitation;
  showTranscriptOnly?: boolean;
}): ConversationSection => {
  if (showTranscriptOnly || hasHighlightedCitation(highlightedCitation)) {
    return 'transcript';
  }

  return 'summary';
};

export const useConversationNavigation = ({ initialCitation, showTranscriptOnly }: UseConversationNavigationParams) => {
  const [selectedSection, setSelectedSection] = useState<ConversationSection>(() =>
    getDefaultSection({
      highlightedCitation: initialCitation,
      showTranscriptOnly,
    })
  );

  const [highlightedCitation, setHighlightedCitation] = useState<HighlightedCitation | undefined>(initialCitation);

  useEffect(() => {
    if (hasHighlightedCitation(initialCitation)) {
      setHighlightedCitation(initialCitation);
      setSelectedSection('transcript');
      return;
    }

    setHighlightedCitation(undefined);
  }, [initialCitation]);

  // Calls that only have Transcript should always stay on Transcript.
  useEffect(() => {
    if (showTranscriptOnly) {
      setSelectedSection('transcript');
    }
  }, [showTranscriptOnly]);

  const showCitation = (citation: HighlightedCitation) => {
    setHighlightedCitation(citation);
    setSelectedSection('transcript');
  };

  return {
    selectedSection,
    highlightedCitation,
    selectSection: setSelectedSection,
    showCitation,
  };
};
